import React from 'react';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import StoreIcon from '@mui/icons-material/Store';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HistoryIcon from '@mui/icons-material/History';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SecurityIcon from '@mui/icons-material/Security';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DescriptionIcon from '@mui/icons-material/Description';

export type Portal = 'admin' | 'reports' | 'accounting' | 'insurance';

export interface NavItem {
  label: string;
  path: string;
  icon: React.ReactElement;
  match?: string;
  /** Portal this item belongs to */
  portal: Portal;
  /** Only super admins can see this item */
  requireSuperAdmin?: boolean;
  /**
   * Sub-items rendered as an expandable group under this item. The parent's `path` is only
   * used for active-state matching (it is not itself navigable); clicking the parent toggles
   * the group. Children inherit the parent's portal and super-admin requirement.
   */
  children?: NavItem[];
}

// Single source of truth for all navigation items
const allNavItems: NavItem[] = [
  // ============================================
  // ADMIN PORTAL
  // ============================================

  // Reports - visible to admin
  {
    label: 'Covered RO Parts',
    path: '/reports/covered-parts',
    icon: <AssessmentIcon />,
    portal: 'admin',
  },

  // Admin sections - visible to admin
  { label: 'Dashboard', path: '/admin', icon: <DashboardIcon />, match: '/admin', portal: 'admin' },
  { label: 'Users', path: '/admin/users', icon: <PeopleIcon />, portal: 'admin' },

  // Super Admin only sections
  {
    label: 'Groups & Stores',
    path: '/admin/groups-stores',
    icon: <StoreIcon />,
    portal: 'admin',
    requireSuperAdmin: true,
  },
  {
    label: 'Permissions',
    path: '/admin/permissions',
    icon: <SecurityIcon />,
    portal: 'admin',
    requireSuperAdmin: true,
  },
  {
    label: 'Decoder Pipeline',
    path: '/admin/decoder-pipeline',
    icon: <SettingsIcon />,
    portal: 'admin',
    requireSuperAdmin: true,
  },
  {
    label: 'Classification Logs',
    path: '/admin/classification-logs',
    icon: <VisibilityIcon />,
    portal: 'admin',
    requireSuperAdmin: true,
  },
  {
    label: 'Customer Emails',
    path: '/admin/customer-emails',
    icon: <MailOutlineIcon />,
    portal: 'admin',
    requireSuperAdmin: true,
  },
  {
    label: 'Warranty Processes',
    path: '/admin/warranty',
    icon: <VerifiedUserIcon />,
    portal: 'admin',
    requireSuperAdmin: true,
    children: [
      {
        label: 'Warranty Contracts',
        path: '/admin/warranty/contracts',
        icon: <DescriptionIcon />,
        portal: 'admin',
        requireSuperAdmin: true,
      },
      {
        label: 'Warranty Exports',
        path: '/admin/warranty/exports',
        icon: <UploadFileIcon />,
        portal: 'admin',
        requireSuperAdmin: true,
      },
    ],
  },
  { label: 'Account Settings', path: '/admin/account', icon: <SettingsIcon />, portal: 'admin' },

  // ============================================
  // ACCOUNTING PORTAL
  // ============================================
  {
    label: 'Dashboard',
    path: '/accounting',
    icon: <DashboardIcon />,
    match: '/accounting',
    portal: 'accounting',
  },
  {
    label: 'Download Reports',
    path: '/accounting/reports',
    icon: <DownloadIcon />,
    portal: 'accounting',
  },
  {
    label: 'Account Codes',
    path: '/accounting/account-codes',
    icon: <AccountBalanceIcon />,
    portal: 'accounting',
  },
  {
    label: 'Download History',
    path: '/accounting/history',
    icon: <HistoryIcon />,
    portal: 'accounting',
  },
  {
    label: 'Email Schedules',
    path: '/accounting/email-schedules',
    icon: <EmailIcon />,
    portal: 'accounting',
  },
  {
    label: 'Data Alerts',
    path: '/accounting/alerts',
    icon: <NotificationsActiveIcon />,
    portal: 'accounting',
  },
  {
    label: 'Report Settings',
    path: '/accounting/settings',
    icon: <SettingsIcon />,
    portal: 'accounting',
  },
  {
    label: 'Account Settings',
    path: '/accounting/account',
    icon: <SettingsIcon />,
    portal: 'accounting',
  },

  // ============================================
  // INSURANCE PARTNER PORTAL
  // ============================================
  {
    label: 'Dashboard',
    path: '/partner',
    icon: <DashboardIcon />,
    match: '/partner',
    portal: 'insurance',
  },
  {
    label: 'Download Reports',
    path: '/partner/reports',
    icon: <DownloadIcon />,
    portal: 'insurance',
  },
  {
    label: 'Account Settings',
    path: '/partner/account',
    icon: <SettingsIcon />,
    portal: 'insurance',
  },
];

/**
 * Get filtered navigation items for a specific portal
 *
 * @param portal - The portal to get items for ('admin' | 'accounting' | 'insurance')
 * @param isSuperAdmin - Whether the current user is a super admin
 * @param canAccessPortal - Function to check if user can access a portal
 * @returns Filtered array of NavItems
 */
export const getNavItems = (
  portal: Portal,
  isSuperAdmin: boolean,
  canAccessPortal: (portal: string) => boolean,
): NavItem[] => {
  const isVisible = (item: NavItem): boolean => {
    // Only show items for the requested portal
    if (item.portal !== portal) return false;

    // Super admin sees everything
    if (isSuperAdmin) return true;

    // Hide super admin only items from regular users
    if (item.requireSuperAdmin) return false;

    // Check if user has access to this portal
    return canAccessPortal(item.portal);
  };

  return (
    allNavItems
      .filter(isVisible)
      .map((item) => {
        if (!item.children) return item;
        const children = item.children.filter(isVisible);
        return { ...item, children };
      })
      // A group with no visible children has nothing to show
      .filter((item) => !item.children || item.children.length > 0)
  );
};

/** Depth-first flatten of a nav tree, for matching the current URL against every entry. */
export const flattenNavItems = (items: NavItem[]): NavItem[] =>
  items.flatMap((item) => (item.children ? [item, ...flattenNavItems(item.children)] : [item]));
